// ============================================
// Creator Autofill - Full Generate (ES6)
// 전체 자동 생성 기능
// ============================================

import { showToast, hideGlobalLoading } from '../../utils.js';
import { CURRENCY } from '../../constants.js';
import { getCurrentUser } from '../../app/state.js';
import { ENDPOINTS } from '../api.js';
import { authFetch } from '../../shared/auth-fetch.js';
import {
  getFullGenSelectedGenres, setFullGenSelectedGenres,
  getFullGenSelectedModelId, setFullGenSelectedModelId,
  getImageModelList,
  getCurrentEditorData
} from '../state.js';
import { resetEditorForm } from '../editor.js';
import { showConfirm } from '../../shared/confirm-modal.js';
import {
  renderModelCarousel,
  selectModelInCarousel,
  createTagChipsHTML,
  handleTagChipSelection,
  toggleImageOptionSection,
  toggleDisplaySection,
  calculateAutofillCost,
  updateCostDisplay
} from '../../shared/autofill-utils.js';
import { loadAutofillTags } from './modal.js';
import { showLoadingWithRandomText, applyAutoFilledData } from './handler.js';
import { updateCarbonDisplay } from '../../auth/index.js';

// ============================================
// 태그 칩
// ============================================

export async function initFullGenTagChips() {
  const container = document.getElementById('charFullGenGenreChips');
  if (!container) return;

  const tagsData = await loadAutofillTags();
  if (!tagsData || !tagsData.tags) return;

  container.innerHTML = createTagChipsHTML(tagsData.tags, 'CreatorModule', 'selectFullGenGenre');
  setFullGenSelectedGenres([]);

  // 이벤트 위임 설정
  container.onclick = (e) => {
    const chip = e.target.closest('[data-action="selectTag"]');
    if (chip) {
      selectFullGenGenre(chip);
    }
  };
}

export function selectFullGenGenre(chip) {
  handleTagChipSelection(
    'charFullGenGenreChips',
    chip,
    getFullGenSelectedGenres(),
    setFullGenSelectedGenres
  );
}

// ============================================
// 모델 캐러셀
// ============================================

export function renderFullGenModelCarousel() {
  const imageModelList = getImageModelList();
  const fullGenSelectedModelId = getFullGenSelectedModelId();
  const selectedModel = fullGenSelectedModelId || imageModelList[0]?.id || '';

  renderModelCarousel(
    'charFullGenModelCarousel',
    imageModelList,
    selectedModel,
    'CreatorModule',
    'selectFullGenModel',
    'id',
    'image'
  );

  if (!fullGenSelectedModelId && imageModelList.length > 0) {
    setFullGenSelectedModelId(imageModelList[0].id);
  }
  const hiddenInput = document.getElementById('charFullGenSelectedModel');
  if (hiddenInput) hiddenInput.value = getFullGenSelectedModelId();
}

export function selectFullGenModel(modelId) {
  setFullGenSelectedModelId(modelId);
  selectModelInCarousel('charFullGenModelCarousel', modelId, 'charFullGenSelectedModel');
  updateFullGenCost();
}

// ============================================
// 생성 모드 변경 (라디오 버튼)
// ============================================

export function handleFullGenModeChange() {
  const descriptionMode = document.querySelector('input[name="charFullGenMode"][value="description"]');
  const descSection = document.getElementById('charFullGenDescSection');
  const tagSection = document.getElementById('charFullGenTagSection');

  if (descriptionMode?.checked) {
    // 설명 입력 모드
    if (descSection) descSection.style.display = 'block';
    if (tagSection) tagSection.style.display = 'none';
  } else {
    // 태그 선택 모드
    if (descSection) descSection.style.display = 'none';
    if (tagSection) tagSection.style.display = 'block';
  }
}

// 하위 호환성 (기존 코드에서 호출될 수 있음)
export function toggleFullGenDescription() {
  handleFullGenModeChange();
}

export function toggleFullGenTagOption() {
  handleFullGenModeChange();
}

export function toggleFullGenImageOption() {
  toggleImageOptionSection('charFullGenAutoImage', 'charFullGenImageModelSection');
  updateFullGenCost();
}

// ============================================
// 비용 계산
// ============================================

export function updateFullGenCost() {
  const checkbox = document.getElementById('charFullGenAutoImage');
  const needsImage = checkbox?.checked || false;

  const cost = calculateAutofillCost(5, needsImage, getFullGenSelectedModelId(), getImageModelList(), 'id', 1);
  updateCostDisplay('charFullGenCost', cost);
}

// ============================================
// 전체 자동 생성 실행
// ============================================

export async function confirmFullGenerate() {
  const user = getCurrentUser();
  if (!user) {
    showToast('로그인이 필요합니다');
    return;
  }

  const confirmed = await showConfirm({
    message: '필드에 작성된 모든 정보가 삭제되고 새로 채워집니다.\n계속하시겠습니까?',
    confirmText: '계속',
    isDanger: true
  });
  if (!confirmed) return;

  // closeAutofillModal은 순환 참조 방지를 위해 직접 import하지 않고 동적으로 가져옴
  const { closeAutofillModal } = await import('./modal.js');
  closeAutofillModal();

  const btn = document.querySelector('.editor-autofill-btn');
  if (btn) {
    btn.disabled = true;
    btn.classList.add('loading');
  }

  // 라디오 버튼으로 모드 확인
  const isDescriptionMode = document.querySelector('input[name="charFullGenMode"][value="description"]')?.checked ?? true;

  const description = isDescriptionMode
    ? (document.getElementById('charFullGenDescription')?.value || '')
    : '';
  const autoGenerateImage = document.getElementById('charFullGenAutoImage')?.checked || false;
  const showThoughts = document.getElementById('editorShowThoughts')?.checked || false;

  showLoadingWithRandomText();

  try {
    const requestData = {
      fullGenerate: true,
      description: description,
      gender: document.getElementById('editorCharGender')?.value || '여성',
      needsImage: autoGenerateImage,
      needsIntro: true,
      showThoughts: showThoughts,
      imageModelId: autoGenerateImage ? getFullGenSelectedModelId() : null,
      genres: !isDescriptionMode ? getFullGenSelectedGenres() : null
    };

    const response = await authFetch(ENDPOINTS.autoFillCreation, {
      body: requestData
    });

    const result = await response.json();

    if (!response.ok) {
      if (response.status === 402) {
        showToast(`${CURRENCY.NAME}가 부족합니다 (필요: ${result.required}, 현재: ${result.current})`);
      } else {
        // error가 객체일 수 있음 (인증 에러 등)
        const errorMsg = typeof result.error === 'string'
          ? result.error
          : (result.error?.message || '전체 자동 생성 실패');
        showToast(errorMsg);
      }
      return;
    }

    if (result.success && result.data) {
      const currentEditorData = getCurrentEditorData();
      currentEditorData.id = null;
      currentEditorData.imageUrl = null;
      currentEditorData.imageFile = null;
      resetEditorForm();
      // 새 필드 구조: name, age, relationship, prompt, worldPrompt
      const allFields = ['name', 'age', 'relationship', 'prompt', 'worldPrompt'];
      applyAutoFilledData(result.data, allFields, true, false);

      if (result.data.imageRefunded) {
        showToast('생성 완료 (이미지 생성 실패로 이미지 비용 환불됨)');
      } else {
        showToast('전체 자동 생성이 완료되었습니다');
      }

      if (typeof result.carbon === 'number') {
        updateCarbonDisplay(result.carbon);
      }
    }
  } catch (error) {
    console.error('전체 자동 생성 오류:', error);
    showToast('전체 자동 생성 중 오류가 발생했습니다');
  } finally {
    hideGlobalLoading();
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('loading');
    }
  }
}
